import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthModal from '../../components/AuthModal/AuthModal';
import { EmptyShareState, PublicPageCTA, PublicShareLayout } from '../../components/PublicShare/PublicShareLayout';
import ShareMenu from '../../components/PublicShare/ShareMenu';
import AuthGateAction from '../../components/PublicShare/AuthGateAction';
import { CoinPreview } from '../../components/PublicShare/PreviewCards';
import AppPromotionCard from '../../components/AppPromotionCard/AppPromotionCard';
import { useAuth } from '../../context/AuthContext';
import { useAuthGate } from '../../hooks/useAuthGate';
import { PublicShareService } from '../../services/publicShareService';
import { AUTH_MESSAGES } from '../../utils/authMessages';
import { buildOgImageUrl, useShareMetadata } from '../../utils/shareMetadata';
import styles from '../../components/PublicShare/PublicShare.module.css';
import { getShareAsset, isSignatureCoin, SHARE_DESTINATIONS } from '../../utils/coinMedia';
import { seasonalEditionLabel, withSeasonLabel } from '@aluvo/constants/src/collectionAvailability';

export default function PublicCoinShare() {
    const { coinId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { gateAction, authModalProps } = useAuthGate(user);
    const [payload, setPayload] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        PublicShareService.getCoin(coinId)
            .then((data) => mounted && setPayload(data))
            .catch((err) => mounted && setError(err.message || 'This coin could not be found.'));
        return () => { mounted = false; };
    }, [coinId]);

    const coin = payload?.coin;
    const share = useMemo(() => {
        if (!coin) return { title: 'Aluvo Coin | Aluvo', description: 'A verified coin on Aluvo.' };
        const title = coin.tier === 'legendary'
            ? `${coin.user?.displayName || coin.user?.username || 'A collector'} unlocked LEGENDARY for ${coin.title} 💎 | Aluvo`
            : `${coin.user?.displayName || coin.user?.username || 'A collector'} earned ${coin.tierLabel} for ${coin.title} | Aluvo`;
        const text = withSeasonLabel(coin.tier === 'legendary'
            ? `@${coin.user?.username} unlocked a LEGENDARY ${coin.title} coin on Aluvo 💎`
            : `@${coin.user?.username} earned ${coin.tierLabel} for ${coin.title} on Aluvo.`, coin.collection);
        return {
            url: `${window.location.origin}/coin/${coin.shareCode || coin.id}`,
            title,
            description: coin.tier === 'legendary' ? 'Perfect score twice. Legendary mastery achieved on Aluvo.' : 'Verified by challenge. Earned as part of an Aluvo collection.',
            text,
            tier: coin.tier,
            // Link previews always use still artwork (the poster for a Signature coin).
            image: getShareAsset({ coin, destination: SHARE_DESTINATIONS.LINK_PREVIEW })?.url || buildOgImageUrl({ title: coin.title, subtitle: text, username: coin.user?.username, tier: coin.tier, theme: coin.tier === 'legendary' ? 'legendary' : 'cinematic' }),
        };
    }, [coin]);

    useShareMetadata(share);

    if (error) return <EmptyShareState title="This coin could not be found." message="The shared coin may have been removed or is no longer public." />;
    if (!payload) return <EmptyShareState title="Loading coin" message="Polishing the coin visual..." />;
    if (payload.isPrivate) return <EmptyShareState icon="🔒" title="This collector's profile is private." message="This shared coin is not public right now." />;

    const isLegendary = coin.tier === 'legendary';
    const edition = seasonalEditionLabel(coin.collection);
    const quizPath = coin.media ? `/quiz/${coin.media.kind}-${coin.media.tmdbId}${coin.media.seasonNumber ? `-s${coin.media.seasonNumber}` : ''}` : '/discover';

    return (
        <PublicShareLayout theme={isLegendary ? 'legendary' : 'cinematic'} eyebrow={isLegendary ? 'Legendary Unlock' : 'Coin Proof'}>
            <section className={styles.heroCard}>
                <div>
                    <span className={styles.statusPill}>{isSignatureCoin(coin) ? '💎 Signature Coin' : isLegendary ? 'LEGENDARY' : coin.tierLabel}</span>
                    {edition && <span className={styles.pill} data-testid="season-edition">{edition}</span>}
                    <h1 className={styles.title}>{coin.headline}</h1>
                    <p className={styles.subtitle}>{coin.rarityCopy}</p>
                    <div className={styles.metaRow}>
                        <Meta label="Earned" value={formatDate(coin.earnedAt)} />
                        <Meta label="Challenge score" value={coin.quizScore != null ? `${coin.quizScore}%` : 'Verified'} />
                        <Meta label="Upgrade" value={coin.upgradeStatus} />
                    </div>
                    <div className={styles.heroActions}>
                        <ShareMenu {...share} coin={coin} compact />
                        <Link className={styles.secondaryAction} to={`/u/${coin.user?.username}`}>View Profile</Link>
                        <AuthGateAction className={styles.primaryAction} gateAction={gateAction} authMessage={AUTH_MESSAGES.QUIZ} onClick={() => navigate(quizPath)}>Take Quiz</AuthGateAction>
                        <AuthGateAction className={styles.ghostAction} gateAction={gateAction} authMessage={AUTH_MESSAGES.WATCHLIST}>Add to Watchlist</AuthGateAction>
                    </div>
                </div>
                <div className={styles.panel}>
                    <CoinPreview coin={coin} size="lg" legendary={isLegendary} animate />
                    {coin.media?.posterUrl && <img className={styles.posterThumb} src={coin.media.posterUrl} alt={coin.title} />}
                </div>
            </section>
            <AppPromotionCard variant="share_page" compact />
            <PublicPageCTA title="Verify this title and earn your own coin." text="Create your Aluvo account, pass the challenge, and start building a verified movie & TV identity." buttonText="Start earning coins" />
            <AuthModal {...authModalProps} />
        </PublicShareLayout>
    );
}

function Meta({ label, value }) {
    return <div className={styles.stat}><span className={styles.metaLabel}>{label}</span><strong style={{ fontSize: '1rem' }}>{value}</strong></div>;
}
function formatDate(date) {
    if (!date) return 'Recently';
    return new Intl.DateTimeFormat(undefined, { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(date));
}